import React, { useState, useContext, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Flex, Row, Col, Button, Spin, Typography, message, Popconfirm, Space, Avatar, Switch } from "antd";
import { CloseOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import AuthContext from "../context/authContext";
import { sanitizeTtsText, useSentenceReadAloud } from "../context/ttsService";
import { UnityContext } from "../context/unityContext";
import Illusmaker from "../components/Illusmaker";
import AufnehmenPanel from "../components/StudioRecorderPanel";
import CharacterImage from "../components/CharacterImage";
import streenTheme from "../css/theme.scss";
import "../css/style.scss";
import create_enrichment_icon from "../img/gestalten_icons/create_enrichment.svg";
import delete_enrichment_icon from "../img/gestalten_icons/delete_enrichment.svg";
import delete_enrichment_disable_icon from "../img/gestalten_icons/delete_enrichment_disable.svg";
import start_speech_icon from "../img/gestalten_icons/start_speech.svg";
import stop_speech_icon from "../img/gestalten_icons/stop_speech.svg";
import delete_popup_icon from "../img/gestalten_icons/delete_popup_icon.svg";
import cancel_popup_icon from "../img/gestalten_icons/cancel_popup_icon.svg";
import delete_aufnehmen_icon from "../img/aufnehmen_icons/delete_aufnehmen.svg";
import delete_aufnehmen_disable_icon from "../img/aufnehmen_icons/delete_aufnehmen_disable.svg";
import OperationsMenu from "../components/OperationMenu";
// Operation modes enum
import { OPERATION_MODES } from "../config/operationModes";

const { Text } = Typography;

const verbose = true; // set to true to enable debug logging
const vlog = (...args) => verbose && console.log(...args);

const Studio = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);

  const { t } = useTranslation();

  const { selectedSentences, selectedCharacters = [], sentence, wordSpanId, groupId, storyTitle, selectedStory, selectedOperation } =
    location.state || {};

  const illusRef = useRef(null);
  const { startSentence, stopSentence, isSentenceSpeaking, getSentenceWordIndex } =
    useSentenceReadAloud();
  const unityContextValue = UnityContext();
  const { sendMessage } = unityContextValue;
  const [selectedEnrichment, setSelectedEnrichment] = useState(undefined);
  const selectedEnrichmentRef = useRef(undefined);
  const sentenceEnrichmentRef = useRef([]);
  const lastEnrichmentFetchKeyRef = useRef(null);
  const enrichmentFetchInFlightRef = useRef(false);
  const pendingSentenceSwitchRef = useRef(false);
  const [sentenceEnrichments, setSentenceEnrichments] = useState([]);
  const [enrichmentsBySentence, setEnrichmentsBySentence] = useState({});
  const [recordingsBySentence, setRecordingsBySentence] = useState({});
  // const [selectedWordSpanID, setSelectedWordSpanID] = useState(wordSpanId || null);

  const selectedWordSpanID = useRef(null);
  const [changeDetected, setChangeDetected] = useState(false);
  const [assignedUsers, setAssignedUsers] = useState({});
  const [isNoEnrichmentOverlayDismissed, setIsNoEnrichmentOverlayDismissed] = useState(false);
  const [isMultipleEnrichmentPerSentence, setIsMultipleEnrichmentPerSentence] = useState(false);

  // Load selectedStudioOperation from localStorage or use selectedOperation as default
  const [selectedStudioOperation, setSelectedStudioOperation] = useState(() => {
    const savedOperation = localStorage.getItem('selectedStudioOperation');
    // If we have a selectedOperation from location.state, use that instead of localStorage
    // This handles cases where user navigates directly to record mode
    if (selectedOperation && selectedOperation !== OPERATION_MODES.GESTALTEN) {
      localStorage.setItem('selectedStudioOperation', selectedOperation);
      return selectedOperation;
    }
    return savedOperation || OPERATION_MODES.GESTALTEN; // default to GESTALTEN if nothing saved
  });
  const setNavToGestaltenPage = () => {};
  const handleSelectOperation = (operation) => {
    setSelectedStudioOperation(operation);
    // Save to localStorage so it persists across page refreshes
    localStorage.setItem('selectedStudioOperation', operation);
    
    // Handle navigation for reading space modes
    const readingSpaceModes = [OPERATION_MODES.AUSLOSEN, OPERATION_MODES.VORLESEN, OPERATION_MODES.SAMMELN];
    if (readingSpaceModes.includes(operation)) {
      // Navigate to StoryReader with current state
      navigate("/StoryReader", { state: location.state });
    }
  };

  const handleSelectedWordSpanIDChange = (wordSpanID) => {
    vlog("[Gestalten] Selected word span id:", wordSpanID);
    selectedWordSpanID.current = wordSpanID;
  };

  const handleSelectedWordButtonClick = useCallback((wordSpanID, enrichments = sentenceEnrichmentRef.current) => {
    vlog("handleSelectedWordButtonClick:::", wordSpanID);

    selectedWordSpanID.current = wordSpanID;

    const enrichment =
      enrichments.find((e) => e.wordspanid === wordSpanID) ||
      sentenceEnrichmentRef?.current?.find((e) => e.wordspanid === wordSpanID);
    vlog("[Gestalten] Word selection resolved enrichment:", {
      wordSpanID,
      enrichmentId: enrichment?.id,
      sentenceId: enrichment?.sentenceid,
    });
    setSelectedEnrichment(enrichment || undefined);
    selectedEnrichmentRef.current = enrichment;
  }, []);

  const syncSentenceSelection = useCallback((activeSentence, enrichments = []) => {
    const defaultWordSpanID = activeSentence?.id
      ? `word_${activeSentence.id}_0`
      : null;

    let latestEnrichment = null;
    const sentenceScopedEnrichments = enrichments.filter(
      (e) => Number(e.sentenceid) === Number(activeSentence?.id)
    );
    const fallbackEnrichment =
      sentenceScopedEnrichments.length === 0 ? enrichments[0] : null;

    latestEnrichment = sentenceScopedEnrichments[sentenceScopedEnrichments.length - 1] || null;

    const latestEnrichmentWordSpanID = latestEnrichment?.wordspanid || null;

    vlog("[Gestalten] Syncing sentence selection", {
      sentenceId: activeSentence?.id,
      isMultipleEnrichmentPerSentence,
      enrichmentCount: enrichments.length,
      latestEnrichmentWordSpanID,
      fallbackEnrichmentId: fallbackEnrichment?.id,
      defaultWordSpanID,
    });

    if (latestEnrichmentWordSpanID) {
      selectedEnrichmentRef.current = latestEnrichment;
      setSelectedEnrichment(latestEnrichment);
      selectedWordSpanID.current = latestEnrichmentWordSpanID;
      handleSelectedWordButtonClick(latestEnrichmentWordSpanID, enrichments);
      vlog("[Gestalten] Loaded existing enrichment for sentence:", latestEnrichmentWordSpanID);
      return;
    }

    // Load the latest previous scene as a baseline, but do not mark it as the
    // current sentence's enrichment. Saving must create a new enrichment here.
    selectedEnrichmentRef.current = undefined;
    setSelectedEnrichment(fallbackEnrichment || undefined);
    selectedWordSpanID.current = defaultWordSpanID;

    if (defaultWordSpanID) {
      vlog("[Gestalten] No own enrichment found. Selected first word span:", defaultWordSpanID);
    } else {
      vlog("[Gestalten] No active sentence available for selection sync.");
    }

    if (fallbackEnrichment) {
      vlog("[Gestalten] Loading fallback enrichment scene:", {
        fallbackEnrichmentId: fallbackEnrichment.id,
        fallbackSentenceId: fallbackEnrichment.sentenceid,
      });
      return;
    }

    if (pendingSentenceSwitchRef.current) {
      setIsLoadingEnrichment(false);
      pendingSentenceSwitchRef.current = false;
    }
  }, [handleSelectedWordButtonClick, isMultipleEnrichmentPerSentence]);

  const [isSavingEnrichment, setIsSavingEnrichment] = useState(false);
  const [isLoadingEnrichment, setIsLoadingEnrichment] = useState(false);
  const [resetMode, setResetMode] = useState(false);
  const [recordingRefreshKey, setRecordingRefreshKey] = useState(0);
  const initialIndex = selectedSentences?.findIndex((s) => s.id === sentence?.id) ?? 0;
  const [currentSentenceIndex, setCurrentSentenceIndex] = useState(initialIndex >= 0 ? initialIndex : 0);

  const fetchAllEnrichments = useCallback(async () => {
    if (!currentUser || !selectedSentences?.length) {
      return;
    }

    try {
      const response = await axios.get(`/api/enrichments`, {
        params: {
          storyid: selectedSentences?.[0]?.storyid,
          // sentenceid: selectedSentences.id,
          userid: currentUser.id, // Needed to check private enrichments
          groupId: Number(groupId) || 0, // Needed to check group enrichments
        },
      });

      vlog("[Gestalten] All enrichments fetched:", response);
      const selectedSentenceIds = new Set(
        selectedSentences.map((selectedSentence) => Number(selectedSentence.id))
      );
      const groupedEnrichments = (response.data || [])
        .filter((enrichment) => selectedSentenceIds.has(Number(enrichment?.sentenceid)))
        .reduce((acc, enrichment) => {
          const sentenceId = enrichment?.sentenceid;
          if (sentenceId) {
            acc[sentenceId] = [...(acc[sentenceId] || []), enrichment];
          }
          return acc;
        }, {});

      setEnrichmentsBySentence(groupedEnrichments);
      vlog("[Gestalten] Enrichments grouped by sentence:", groupedEnrichments);
    } catch (error) {
      console.error("Error fetching all enrichments", error);
    }

    // vlog("[Gestalten] Fetching all enrichments for story:", selectedSentences);
  }, [currentUser, groupId, selectedSentences]);

  const fetchAllRecordings = useCallback(async () => {
    if (!currentUser || !selectedSentences?.length) {
      return;
    }

    try {
      // Fetch recordings for the entire story
      const storyId = selectedSentences?.[0]?.storyid;
      if (!storyId) return;
      vlog("[Gestalten] Fetching all recordings for story:", storyId, "and user:", currentUser.id);
      const response = await axios.get(`/api/audioRecords/filter`, {
        params: {
          userId: currentUser.id,
          storyId: storyId,
          groupId: Number(groupId) || 0, // Needed to check group recordings
        },
      });

      vlog("[Gestalten] All recordings fetched:", response);
      
      // Group recordings by sentence_id
      const groupedRecordings = (response.data || []).reduce((acc, recording) => {
        const sentenceId = recording?.sentence_id;
        if (sentenceId) {
          if (!acc[sentenceId]) {
            acc[sentenceId] = [];
          }
          acc[sentenceId].push(recording);
        }
        return acc;
      }, {});

      setRecordingsBySentence(groupedRecordings);
      vlog("[Gestalten] Recordings grouped by sentence:", groupedRecordings);
    } catch (error) {
      console.error("Error fetching all recordings", error);
    }
  }, [currentUser, groupId, selectedSentences]);

  const handleEnrichmentChange = (enrichments) => {
    setSentenceEnrichments(enrichments);

    const activeSentence = selectedSentences?.[currentSentenceIndex] || sentence;
    if (activeSentence?.id) {
      setEnrichmentsBySentence((previousEnrichmentsBySentence) => {
        if (enrichments.length === 0) {
          const nextEnrichmentsBySentence = { ...previousEnrichmentsBySentence };
          delete nextEnrichmentsBySentence[activeSentence.id];
          vlog("[Gestalten] enrichmentsBySentence:", enrichmentsBySentence);
          return nextEnrichmentsBySentence;
        }

        vlog("[Gestalten] enrichmentsBySentence:previousEnrichmentsBySentence::", enrichmentsBySentence);
        return {
          ...previousEnrichmentsBySentence,
          [activeSentence.id]: enrichments,
        };
      });
    }
  };

  const fetchEnrichments = useCallback(async () => {
    const activeSentence = selectedSentences?.[currentSentenceIndex] || sentence;
    if (!activeSentence || !currentUser) {
      return;
    }

    const fetchKey = `${activeSentence.storyid}_${activeSentence.id}_${currentUser.id}_${Number(groupId) || 0}`;
    if (enrichmentFetchInFlightRef.current && lastEnrichmentFetchKeyRef.current === fetchKey) {
      vlog("[Gestalten] Skipping duplicate enrichment fetch (in-flight):", fetchKey);
      return;
    }

    try {
      enrichmentFetchInFlightRef.current = true;
      lastEnrichmentFetchKeyRef.current = fetchKey;
      vlog("[Gestalten] Fetch enrichments:", fetchKey);
      const response = await axios.get(`/api/enrichments`, {
        params: {
          storyid: activeSentence.storyid,
          sentenceid: activeSentence.id,
          userid: currentUser.id, // Needed to check private enrichments
          groupId: Number(groupId) || 0, // Needed to check group enrichments
        },
      });

      const sortedEnrichments = [...(response.data || [])].sort(
        (a, b) => Number(b.sentenceid) - Number(a.sentenceid)
      );

      setSentenceEnrichments(sortedEnrichments);
      sentenceEnrichmentRef.current = sortedEnrichments;
      vlog("[Gestalten] Enrichments fetched:", sortedEnrichments.length);
      syncSentenceSelection(activeSentence, sortedEnrichments);
      if (pendingSentenceSwitchRef.current && sortedEnrichments.length === 0) {
        // Nothing to load in Unity for this sentence.
        setIsLoadingEnrichment(false);
        pendingSentenceSwitchRef.current = false;
      }
      if (sortedEnrichments.length > 0) {
        vlog("respone.data:::", sortedEnrichments);
      }
    } catch (error) {
      console.error("Error fetching enrichments", error);
      if (pendingSentenceSwitchRef.current) {
        setIsLoadingEnrichment(false);
        pendingSentenceSwitchRef.current = false;
      }
    } finally {
      enrichmentFetchInFlightRef.current = false;
    }
  }, [currentSentenceIndex, currentUser, groupId, selectedSentences, sentence, syncSentenceSelection]);

  useEffect(() => {
    fetchEnrichments();
    fetchAllRecordings();
    fetchAllEnrichments();
  }, [fetchEnrichments, fetchAllRecordings, fetchAllEnrichments]);

  useEffect(() => {
    if (isMultipleEnrichmentPerSentence) {
      return;
    }

    const activeSentence = selectedSentences?.[currentSentenceIndex] || sentence;
    if (!activeSentence?.id || selectedWordSpanID.current) {
      return;
    }

    const defaultWordSpanID = `word_${activeSentence.id}_0`;
    selectedWordSpanID.current = defaultWordSpanID;
    vlog("[Gestalten] Default selected word span id (single mode):", defaultWordSpanID);
  }, [isMultipleEnrichmentPerSentence, currentSentenceIndex, selectedSentences, sentence]);

  useEffect(() => {
    vlog("[Gestalten] Current selected enrichment span id:", selectedEnrichment?.wordspanid || null);
  }, [selectedEnrichment]);

  // Reset resetMode when sentence changes
  useEffect(() => {
    setResetMode(false);
  }, [currentSentenceIndex]);

  useEffect(() => {
    const fetchAssignedUsers = async () => {
      const storyId = selectedStory?.storyId || sentence?.storyid;

      if (!groupId || !storyId || !currentUser) {
        return;
      }

      try {
        const response = await axios.get(
          `/api/tasks/groups/${groupId}/tasks-with-users`,
          {
            params: { storyId },
          }
        );

        const usersMap = {};

        response.data.forEach((task) => {
          if (!task.character_id) {
            return;
          }

          if (!usersMap[task.character_id]) {
            usersMap[task.character_id] = {};
          }

          if (!usersMap[task.character_id][task.member_id]) {
            usersMap[task.character_id][task.member_id] = {
              userId: task.member_id,
              username: task.username,
              tasks: [],
            };
          }

          usersMap[task.character_id][task.member_id].tasks.push(task.category);
        });

        setAssignedUsers(usersMap);
      } catch (error) {
        console.error("Error fetching tasks:", error);
      }
    };
    
    fetchAssignedUsers();
    vlog("[Gestalten] Fetching assigned users for group:", groupId, "and story:", selectedStory?.storyId || sentence?.storyid);
  }, [currentUser, groupId, selectedStory, sentence]);

  const switchToSentence = useCallback(async (targetIndex) => {
    if (
      targetIndex < 0 ||
      targetIndex >= selectedSentences.length ||
      targetIndex === currentSentenceIndex
    ) {
      return;
    }

    stopSentence();
    pendingSentenceSwitchRef.current = true;
    setIsLoadingEnrichment(true);
    // Trigger GetSceneState to detect changes before switching
    sendMessage("Manager_SaveLoad", "GetSceneState");

    try {
      vlog("[Gestalten] Checking for unsaved enrichment changes before sentence switch.");
      await illusRef.current?.saveEnrichmentIfChanged();
      vlog("[Gestalten] Unsaved enrichment check finished before sentence switch.");
    } catch (error) {
      console.error("Failed to save enrichment before sentence switch:", error);
      pendingSentenceSwitchRef.current = false;
      setIsLoadingEnrichment(false);
      message.error("Speichern fehlgeschlagen. Der Satz wurde nicht gewechselt.");
      return;
    }

    const targetSentence = selectedSentences[targetIndex];
    const targetDefaultWordSpanID = targetSentence?.id
      ? `word_${targetSentence.id}_0`
      : null;

    vlog("[Gestalten] Switching sentence", {
      fromSentenceId: selectedSentences[currentSentenceIndex]?.id,
      toSentenceId: targetSentence?.id,
    });

    setIsNoEnrichmentOverlayDismissed(false);
    selectedEnrichmentRef.current = undefined;
    setSelectedEnrichment(undefined);
    selectedWordSpanID.current = targetDefaultWordSpanID;
    setCurrentSentenceIndex(targetIndex);
  }, [currentSentenceIndex, selectedSentences, stopSentence]);

  const handlePrevSentence = () => {
    if (currentSentenceIndex > 0) {
      switchToSentence(currentSentenceIndex - 1);
      console.log("Prev sentence:", selectedSentences[currentSentenceIndex - 1].text);
    }
  };

  const handleNextSentence = () => {
    if (currentSentenceIndex < selectedSentences.length - 1) {
      switchToSentence(currentSentenceIndex + 1);
      console.log("Next sentence:", selectedSentences[currentSentenceIndex + 1]);

    }
  };

  const handleAddEnrichment = async (sent, wordSpanIndex, blobData, enrichment) => {
    try {
      const data = {
        storyid: sent.storyid,
        sentenceid: sent.id,
        userid: currentUser.id,
        wordspanid: wordSpanIndex,
        type: "default",
        specification: "default",
        illusdata: blobData,
        illusdata_str: blobData,
        group_id: groupId || 0,
        visibility: "group",
      };
      const shouldUpdateExistingEnrichment =
        enrichment?.id &&
        Number(enrichment.sentenceid) === Number(sent.id) &&
        enrichment.wordspanid === wordSpanIndex;

      if (!shouldUpdateExistingEnrichment) {
        vlog("[Gestalten] Adding new enrichment:", data);
        await axios.post(`/api/enrichments/`, data);
      } else {
        vlog("[Gestalten] Updating existing enrichment:", { ...data, id: enrichment.id });
        await axios.put(`/api/enrichments/${enrichment.id}`, data);
      }
    } catch (error) {
      console.error("Error saving enrichment:", error);
      message.error("Fehler beim Speichern.");
    } finally {
      setIsSavingEnrichment(false);
    }
  };

  const handleDeleteEnrichmentCallback = async (enrichmentId) => {
    try {
      await fetchAllEnrichments();
      await fetchEnrichments();
    } catch (error) {
      console.error("Error deleting enrichment:", error);
      message.error("Fehler beim Löschen.");
    }
  };

  const handleDeleteRecording = async () => {
    try {
      // Find the recording for the current sentence
      const currentSentenceRecordings = recordingsBySentence[currentSentence?.id] || [];
      if (currentSentenceRecordings.length === 0) {
        message.error("Keine Aufnahme zum Löschen gefunden.");
        return;
      }

      // Delete the first recording (or all recordings for this sentence)
      const recordingToDelete = currentSentenceRecordings[0];
      await axios.delete(`/api/audioRecords/${recordingToDelete.id}`);
      
      // Refresh recordings
      await fetchAllRecordings();
      setRecordingRefreshKey((prev) => prev + 1);
      message.success("Aufnahme erfolgreich gelöscht.");
    } catch (error) {
      console.error("Error deleting recording:", error);
      message.error("Fehler beim Löschen der Aufnahme.");
    }
  };

  const handleBack = () => {
    stopSentence();
    // Optionally clear the saved operation when navigating away
    localStorage.removeItem('selectedStudioOperation');
    vlog("[Gestalten] Navigating back to StoryReader with state:", location.state);

    const returnState = {
      ...(location.state || {}),
      ...(selectedStory || {}),
      storyId: selectedStory?.storyId || location.state?.storyId || currentSentence?.storyid || null,
      groupId: groupId ?? location.state?.groupId ?? null,
      // can be needed later
      // taskCategory: location.state?.taskCategory || selectedStory?.taskCategory || selectedOperation || location.state?.selectedOperation || null,
      title: storyTitle || location.state?.storyTitle || null,
      img: selectedStory?.img || location.state?.img || null,
      activity: location.state?.activity || selectedStory?.activity || null,
      // Pass the selected studio operation when navigating back
      selectedOperation: selectedStudioOperation || location.state?.selectedOperation || null,
    };

    navigate("/StoryReader", { state: returnState });
  };

  const handleEnrichmentModeSwitch = (checked) => {
    setIsMultipleEnrichmentPerSentence(checked);
    console.log(
      "Enrichment mode:",
      checked ? "multiple enrichments per sentence" : "single enrichment per sentence"
    );
  };

  const handleEnrichmentSceneLoaded = useCallback(() => {
    if (!pendingSentenceSwitchRef.current) {
      return;
    }

    setIsLoadingEnrichment(false);
    pendingSentenceSwitchRef.current = false;
  }, []);

  const isAufnehmenMode = selectedStudioOperation === OPERATION_MODES.AUFNEHMEN;
  const prevIsAufnehmenModeRef = useRef(isAufnehmenMode);
  
  // Debug log to check current operation
  vlog("Current selectedStudioOperation:", selectedStudioOperation);

  // Only stop sentence when switching away from Aufnehmen mode, not when entering it
  // This allows manual TTS activation even in recording mode
  useEffect(() => {
    if (!isAufnehmenMode && prevIsAufnehmenModeRef.current) {
      stopSentence();
    }
    prevIsAufnehmenModeRef.current = isAufnehmenMode;
  }, [isAufnehmenMode, stopSentence]);

  if (!sentence) {
    navigate("/");
    return null;
  }

  const cleanText = (text) => sanitizeTtsText(text);
  const sentenceFontFamily = streenTheme?.secondaryFont || "'Lora', serif";
  const currentSentence = selectedSentences?.[currentSentenceIndex];
  const sentencePlainText = sanitizeTtsText(currentSentence?.text ?? "");
  const selectedWordSpanIDValue =
    selectedWordSpanID.current ||
    (!isMultipleEnrichmentPerSentence && currentSentence?.id
      ? `word_${currentSentence.id}_0`
      : null);
  const activeWordIndex = getSentenceWordIndex(
    currentSentence?.id,
    currentSentence?.text ?? ""
  );
  const currentCharacter = selectedCharacters.find((c) => c.id === currentSentence?.characterid) || null;
  const usersForCharacter = Object.values(assignedUsers[currentSentence?.characterid] || {});
  const currentAuthoringUser =
    usersForCharacter.find(
      (user) =>
        user.userId === currentUser.id &&
        user.tasks.some((task) => task.toLowerCase() === "enrichments")
    ) ||
    usersForCharacter.find((user) =>
      user.tasks.some((task) => task.toLowerCase() === "enrichments")
    ) ||
    null;
  const hasSelectedEnrichment =
    selectedEnrichment?.sentenceid === currentSentence?.id &&
    selectedEnrichment?.wordspanid === selectedWordSpanIDValue;
  const shouldShowNoEnrichmentOverlay =
    !isSavingEnrichment && !isLoadingEnrichment && currentSentence?.id && !hasSelectedEnrichment && !isNoEnrichmentOverlayDismissed;

  const hasAssignedRecordingTask = usersForCharacter.some(
    (user) =>
      user.userId === currentUser?.id &&
      user.tasks.some((task) => task.toLowerCase() === "record")
  );
  const isGroupContext = groupId != null && groupId !== 0 && groupId !== "0";
  const hasRecordingRights = Boolean(currentUser?.id) && (!isGroupContext || hasAssignedRecordingTask);
  const hasFeedbackRights = hasRecordingRights;
  const isRecordOperation = selectedStudioOperation === OPERATION_MODES.AUFNEHMEN;
  const isDeleteActionEnabled = isRecordOperation
    ? Boolean(recordingsBySentence[currentSentence?.id]?.length)
    : hasSelectedEnrichment;
  const deleteButtonIcon = isRecordOperation
    ? (isDeleteActionEnabled ? delete_aufnehmen_icon : delete_aufnehmen_disable_icon)
    : (isDeleteActionEnabled ? delete_enrichment_icon : delete_enrichment_disable_icon);
  
  const selectedWordLabel = cleanText(currentSentence?.text ?? "")
    .split(" ")
    .find((word, index) =>
      selectedWordSpanIDValue === `word_${selectedSentences[currentSentenceIndex]?.id}_${index}` ? word : null
    );
  const paginationWindowSize = 8;
  const totalSentences = selectedSentences?.length ?? 0;
  const currentWindowStart = Math.floor(currentSentenceIndex / paginationWindowSize) * paginationWindowSize;
  const currentWindowEnd = Math.min(currentWindowStart + paginationWindowSize, totalSentences);
  const visibleSentenceItems = (selectedSentences || []).slice(currentWindowStart, currentWindowEnd);

  const jumpSentenceWindow = (direction) => {
    if (totalSentences === 0) {
      return;
    }

    if (direction === "prev") {
      const prevWindowTargetIndex = Math.max(0, currentWindowStart - paginationWindowSize);
      switchToSentence(prevWindowTargetIndex);
      return;
    }

    const nextWindowTargetIndex = Math.min(totalSentences - 1, currentWindowEnd);
    switchToSentence(nextWindowTargetIndex);
  };

  return (
    <div className="working-space" style={{
      // padding: "6px 10px 0",
      display: "flex",
      flexDirection: "column",
      // gap: "8px"
    }}>
      {/* Header */}
      <Row align="middle" style={{ flexShrink: 0 }}>
        <Col flex="64px">
        <Flex className="operation-menu-fab-btn">
              <OperationsMenu
                setNavToGestaltenPage={setNavToGestaltenPage}
                selectedOperation={selectedOperation}
                onSelectOperation={handleSelectOperation}
                // isEditing={isEditing}
                // setIsEditing={setIsEditing}
              />
            </Flex>
          {/* <OperationsMenu
                    setNavToGestaltenPage={setNavToGestaltenPage}
                    selectedOperation={selectedStudioOperation}
                    onSelectOperation={handleSelectOperation}
                    // isEditing={isEditing}
                    // setIsEditing={setIsEditing}
                  /> */}
        </Col>
        <Col flex="64px">
          <Switch
            size="small"
            checked={isMultipleEnrichmentPerSentence}
            onChange={handleEnrichmentModeSwitch}
            checkedChildren="Mehr"
            unCheckedChildren="1"
            title={
              isMultipleEnrichmentPerSentence
                ? "Mehrere Enrichments pro Satz"
                : "Ein Enrichment pro Satz"
            }
            style={{ visibility: "hidden" /* Hide switch for now, as multiple enrichment mode is not fully implemented yet */ }}
          />
        </Col>
        <Col flex="auto" style={{ textAlign: "center" }}>
          <h2 className="story-title">{storyTitle}</h2>
        </Col>
        <Col flex="64px" style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button className="close-button" size="large" icon={<CloseOutlined />} onClick={handleBack}>
          </Button>
        </Col>
      </Row>

      {/* Selected sentence — character block style */}
      <Row align="middle" justify="center" className="read gestalten-read" style={{ margin: 0, flexShrink: 0, width: "100%", flexWrap: "nowrap", columnGap: 0 }}>
        <Col flex="66px">
          <Button
            className="gestalten-nav-button"
            size="large"
            icon={ <LeftOutlined
              style={{
                fontSize: 35, 
              }}
            />}
            onClick={handlePrevSentence}
            disabled={currentSentenceIndex === 0}
            aria-label="Previous sentence"
          />
        </Col>
        <Col flex="auto">
          <ul className="storycreator-story-list gestalten-story-list" style={{ margin: 0, padding: 0, width: "100%" }}>
            <li className="sentence gestalten-sentence" style={{ listStyle: "none", width: "100%", margin: 0 }}>
              <main
                className="gestalten-sentence-container gestalten-sentence-box"
                style={{
                  margin: 0,
                  width: "100%",
                  fontSize: "1rem",
                  backgroundColor: `${currentCharacter.color}`,
                }}
              >






                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 20,
                    padding: "20px 25px",
                    flexShrink: 0,
                  }}
                >





                  <Button
                    className="sound-button"
                    onClick={() => {
                      if (!currentSentence) return;

                      if (isSentenceSpeaking(currentSentence.id)) {
                        stopSentence();
                        return;
                      }

                      startSentence(currentSentence.id, currentSentence.text);
                    }}
                    title={
                      currentSentence && isSentenceSpeaking(currentSentence.id)
                        ? "Vorlesen stoppen"
                        : "Vorlesen starten"
                    }
                    icon={isSentenceSpeaking(currentSentence?.id) ?
                      <img
                        src={start_speech_icon}
                        alt=""
                        aria-hidden="true"
                        style={{ height: 29, display: "block" }}
                      />
                      :
                      <img
                        src={stop_speech_icon}
                        alt=""
                        aria-hidden="true"
                        style={{ height: 29, display: "block" }}
                      />
                    }
                  >

                  </Button>
                  

                  {currentSentence?.id && (
                    isMultipleEnrichmentPerSentence ? selectedWordLabel : true) && (
                    <Popconfirm
                      overlayClassName="studio-delete-popconfirm"
                      getPopupContainer={() =>
                        isAufnehmenMode
                          ? document.querySelector(".gestalten-auflnehmen-panel") || document.body
                          : document.querySelector(".gestalten-illusmaker-container") || document.body
                      }
                      icon={null}
                      title={isAufnehmenMode ? "Aufnahme löschen" : t("illusmaker:pop_delete_title")}
                      description={isAufnehmenMode 
                        ? "Möchten Sie diese Aufnahme wirklich löschen?" 
                        : t("illusmaker:pop_delete_desc")}
                      onConfirm={isAufnehmenMode 
                        ? handleDeleteRecording 
                        : () => illusRef.current?.handleDeleteEnrichment(selectedWordSpanIDValue)}
                      onCancel={() => { }}
                      okText={
                        <span className="studio-delete-popconfirm-action">
                          <img
                            src={delete_popup_icon}
                            alt=""
                            aria-hidden="true"
                          />
                          Löschen
                        </span>
                      }
                      cancelText={
                        <span className="studio-delete-popconfirm-action">
                          <img
                            src={cancel_popup_icon}
                            alt=""
                            aria-hidden="true"
                          />
                          Abbrechen
                        </span>
                      }
                      okButtonProps={{
                        className: "studio-delete-popconfirm-delete",
                      }}
                      cancelButtonProps={{
                        className: "studio-delete-popconfirm-cancel",
                      }}
                    >
                      <Button
                        // type="primary"
                        // size="large"
                        icon={
                          <img
                            src={deleteButtonIcon}
                            alt=""
                            aria-hidden="true"
                            style={{ height: 29, display: "block" }}
                          />
                        }
                        disabled={!isDeleteActionEnabled}
                        // icon={<DeleteOutlined />}
                        className="delete-button"
                      >
                        {isMultipleEnrichmentPerSentence
                          ? `Löschen: ${selectedWordLabel || "Wort"}`
                          : isAufnehmenMode ? "" : ""}
                      </Button>
                    </Popconfirm>
                  )}
                </div>

                <div className="gestalten-sentence-content" style={{ padding: "6px 8px" }}>
                  {currentCharacter && (
                    <div className="gestalten-top-row" style={{ padding: "4px 6px", maxWidth: "100%" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <div className="gestalten-character-row" style={{ gap: 6 }}>
                          {/* <span className="gestalten-inline-sentence-index">{currentSentence?.ord}</span> */}
                          <CharacterImage
                            filename={currentCharacter.img}
                            name={currentCharacter.name}
                            storyId={currentCharacter.storyId}
                            src={"pubup"}
                          />
                          <aside className="gestalten-character-name" style={{ margin: 0, fontSize: "16px" }}>{currentCharacter.name}:</aside>

                        </div>
                        {
                          groupId !== null && (
                          <Avatar
                          className={
                            currentAuthoringUser?.userId === currentUser.id
                              ? "highlight-avatar"
                              : "normal-avatar"
                          }
                          size="small"
                          // gap={2}
                          style={{}}
                        >
                          {currentAuthoringUser?.username || currentUser?.username || "-"}
                        </Avatar>
                          )
                        }

                      </div>
                      
                    </div>
                  )}
                  <div className="gestalten-text-row" style={{ padding: "2px 6px", maxWidth: "100%" }}>
                    <div style={{ padding: 0, lineHeight: 1.2, fontFamily: sentenceFontFamily }}>
                      {sentencePlainText
                        .split(" ")
                        .map((word, windex) => {
                          const wordSpanID = `word_${currentSentence?.id}_${windex}`;
                          const isSelected = selectedWordSpanIDValue === wordSpanID;
                          const isCurrentlySpokenWord = activeWordIndex === windex;
                          const hasEnrichment = sentenceEnrichments.some(
                            (e) => e.wordspanid === wordSpanID
                          );
                          const btn = (
                            <Button
                              className="gestalten-word-btn"
                              key={wordSpanID}
                              value={word}
                              name={wordSpanID}
                              style={{
                                paddingRight: "2px",
                                paddingLeft: "2px",
                                border: "none",
                                boxShadow: "none",
                                fontSize: "clamp(0.95rem, 1.4vw, 1.1rem)",
                                lineHeight: 1.1,
                                minHeight: 28,
                                textDecoration:
                                  isMultipleEnrichmentPerSentence && hasEnrichment
                                    ? "underline"
                                    : "none",
                                // fontWeight: isSelected ? "normal" : "normal",
                                fontWeight: "normal",
                                fontFamily: sentenceFontFamily,
                                backgroundColor: isCurrentlySpokenWord
                                    ? streenTheme.orange4
                                    : "transparent",
                                color: isSelected ? streenTheme.red1 : "black",
                                cursor: isMultipleEnrichmentPerSentence ? "pointer" : "default",
                              }}
                              variant={isSelected ? "text" : undefined}
                              onClick={() => {
                                if (!isMultipleEnrichmentPerSentence) {
                                  return;
                                }
                                illusRef.current?.handleWordSelect(wordSpanID);
                              }}
                            >
                              {word}
                            </Button>
                          );
                          return (
                            <Space key={wordSpanID} size={2}>
                              {changeDetected && isMultipleEnrichmentPerSentence ? (
                                <Popconfirm
                                  title={t("illusmaker:pop_word_title")}
                                  description={t("illusmaker:pop_word_desc")}
                                  onConfirm={() => {
                                    illusRef.current?.confirmWordChange();
                                    illusRef.current?.handleWordSelect(wordSpanID);
                                  }}
                                  onCancel={() => { }}
                                  okText="Ja"
                                  cancelText="Nein"
                                >
                                  {btn}
                                </Popconfirm>
                              ) : btn}
                            </Space>
                          );
                        })}
                    </div>
                  </div>
                </div>
              </main>
            </li>
          </ul>

        </Col>
        <Col flex="66px">
          <Button
            className="gestalten-nav-button"
            size="large"
            icon={<RightOutlined style={{
              fontSize: 35, 
            }}
          />}
            onClick={handleNextSentence}
            disabled={currentSentenceIndex === selectedSentences.length - 1}
            aria-label="Next sentence"
          />
        </Col>
      </Row>
      {/* Studio Sentence Component */}
      <div className="gestalten-sentence-pagination" aria-label="Satznavigation">
        {currentWindowStart > 0 && (
          <Button
            key="pagination_prev_more"
            type="text"
            className="gestalten-index-button"
            onClick={() => jumpSentenceWindow("prev")}
            aria-label="Vorherige 5 Satze anzeigen"
          >
            ...
          </Button>
        )}

        {visibleSentenceItems.map((sentenceItem, offset) => {
          const index = currentWindowStart + offset;
          const isActiveSentence = index === currentSentenceIndex;
          const hasEnrichment = (enrichmentsBySentence[sentenceItem?.id] || []).length > 0;
          const hasRecording = (recordingsBySentence[sentenceItem?.id] || []).length > 0;
          const sentenceLabel = sentenceItem?.ord || index + 1;
          // vlog("[Gestalten] Rendering sentence pagination item:", {
          //   sentenceItem
          // });
          return (
            <Button
              key={sentenceItem?.id || `sentence_${index}`}
              type="text"
              className={`
                  gestalten-index-button ${isActiveSentence ? "is-active" : ""} 
                  ${hasEnrichment && !isAufnehmenMode ? "gestalten-index-button-enriched" : 
                    hasRecording && isAufnehmenMode ? "gestalten-index-button-recorded" : ""}

                `}
              onClick={() => switchToSentence(index)}
              aria-current={isActiveSentence ? "page" : undefined}
            >
              {sentenceLabel}
            </Button>
          );
        })}

        {currentWindowEnd < totalSentences && (
          <Button
            key="pagination_next_more"
            type="text"
            className="gestalten-index-button"
            onClick={() => jumpSentenceWindow("next")}
            aria-label="Nachste 5 Satze anzeigen"
          >
            ...
          </Button>
        )}
      </div>

      {isAufnehmenMode && (
        <div className="aufnehmen-panel" style={{ marginTop: "-3px" }}>
          <AufnehmenPanel
            key={`${currentSentence?.id ? `recording-${currentSentence.id}` : `recording-${selectedStory?.storyId || "story"}`}-${recordingRefreshKey}`}
            storyId={currentSentence?.storyid || selectedStory?.storyId}
            sentenceId={currentSentence?.id}
            userId={currentUser?.id}
            groupId={groupId == null ? 0 : groupId}
            visibility="group"
            sentenceText={sentencePlainText}
            sentenceOrd={currentSentence?.ord}
            userName={currentUser?.username}
            storyTitle={storyTitle}
            hasRecordingRights={hasRecordingRights}
            hasFeedbackRights={hasFeedbackRights}
            resetMode={resetMode}
            onResetModeChange={setResetMode}
            onRecordingSaved={() => {
              // Refresh recordings after saving
              fetchAllRecordings();
            }}
          />
        </div>
      )}

      {/* Gestalten Component */}
      <div className="gestalten-illusmaker-container" style={{ display: isAufnehmenMode ? "none" : undefined }}>
        {(isSavingEnrichment || isLoadingEnrichment || shouldShowNoEnrichmentOverlay) && (
          <div
            className="gestalten-illusmaker-saving-overlay"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 32,
              zIndex: 10,
              borderRadius: 16,

            }}
          >
            {(isSavingEnrichment || isLoadingEnrichment) && (
              <Spin size="large" style={{ transform: "scale(1.45)", textAlign: "center", }} />
            )}
            <Text class="gestalten-overlay-text" >
              {isSavingEnrichment ? "Speichern..." : isLoadingEnrichment ? "Lade Enrichment..." : "Noch keine Illustration für diesen Satz"}
            </Text>
            {shouldShowNoEnrichmentOverlay && (
              <Button
                // type="primary"
                // size="large"
                icon={
                  <img
                    src={create_enrichment_icon}
                    alt=""
                    aria-hidden="true"
                   style={{ height: 29, display: "block" }}
                  />
                }
                // icon={<DeleteOutlined />}
                onClick={() => setIsNoEnrichmentOverlayDismissed(true)}
                className="gestalten-delete-button"
              >
              </Button>

            )}

          </div>
        )}
        <Illusmaker
          ref={illusRef}
          illusState={true}
          handleIllusModal={handleBack}
          isPlayerMode={groupId !== null && currentAuthoringUser?.userId !== currentUser?.id}
          sentence={currentSentence}
          handleAddEnrichment={handleAddEnrichment}
          sWordSpanID={wordSpanId}
          groupId={groupId}
          isTransparent={false}
          setIsSavingEnrichment={setIsSavingEnrichment}
          onEnrichmentsChange={handleEnrichmentChange}
          sentenceEnrichmentRef={sentenceEnrichmentRef}
          sentenceEnrichments={sentenceEnrichments}
          setSentenceEnrichments={setSentenceEnrichments}
          fetchEnrichments={fetchEnrichments}
          fetchAllEnrichments={fetchAllEnrichments}
          selectedEnrichment={selectedEnrichment}
          setSelectedEnrichment={setSelectedEnrichment}
          selectedEnrichmentRef={selectedEnrichmentRef}
          onSelectedWordChange={handleSelectedWordSpanIDChange}
          onChangeDetected={(detected) => setChangeDetected(detected)}
          handleSelectedWordButtonClick={handleSelectedWordButtonClick}
          selectedWordSpanID={selectedWordSpanID}
          onEnrichmentSceneLoaded={handleEnrichmentSceneLoaded}
          onDeleteEnrichment={handleDeleteEnrichmentCallback}
          unityContextValue={unityContextValue}
        />
      </div>
    </div>
  );
};

export default Studio;
